/**
 * A custom element base class to handle property reactivity and attribute<>property reflection.
 * Based in part on `@lit/reactive-element`
 * @see https://github.com/lit/lit/tree/main/packages/reactive-element
 */

/**
 * @typedef { import('./reactive-element.js').ChangedProperties } ChangedProperties
 * @typedef { import('./reactive-element.js').ReactiveAttributes } ReactiveAttributes
 * @typedef { import('./reactive-element.js').ReactiveController } ReactiveController
 * @typedef { import('./reactive-element.js').ReactiveControllerHost } ReactiveControllerHost
 * @typedef { import('./reactive-element.js').ReactiveHookType<ReactiveElement> } ReactiveHookType
 * @typedef { import('./reactive-element.js').ReactivePrimitiveType } ReactivePrimitiveType
 * @typedef { import('./reactive-element.js').ReactiveProperties } ReactiveProperties
 * @typedef { import('./reactive-element.js').ReactiveType<ReactiveElement> } ReactiveType
 */

import { attributeValueToProperty, isHookPropertyType, propertyValueToAttribute } from './utils.js';
import { isEquivalent } from './is-equivalent.js';
import { ReactiveState } from './reactive-state.js';
import { whenElementParsed } from './when-element-parsed.js';

const GLOBAL_ATTRIBUTES = [
  'accesskey',
  'autocapitalize',
  'class',
  'contenteditable',
  'contextmenu',
  'dir',
  'draggable',
  'dropzone',
  // 'hidden' added by default
  'id',
  'itemprop',
  'lang',
  'slot',
  'spellcheck',
  'style',
  'tabindex',
  'title',
  'translate',
];

// @ts-ignore
// Used as proxy for production mode code.
// Minifier will mangle "param" function parameter, and regex will not match.
const isMinified = !/param/.test(function (param) {});

/**
 * Custom-element base class for tracking and observing attribute and property changes.
 * @implements ReactiveControllerHost
 */
class ReactiveElement extends HTMLElement {
  static __ReactiveElement__ = true;
  /** @type { Map<string, string> } */
  static _attributeToPropertyMap = new Map();
  /** @type { Array<string> } */
  static _observedAttributes;
  /** @type { !ReactiveAttributes } */
  static _reactiveAttributes;
  /** @type { !ReactiveProperties } */
  static _reactiveProperties;

  /**
   * Configure the reactive "attributes" for this element.
   * @returns { ReactiveAttributes }
   */
  static get reactiveAttributes() {
    return {};
  }

  /**
   * Configure the reactive "properties" for this element.
   * @returns { ReactiveProperties }
   */
  static get reactiveProperties() {
    return {};
  }

  /**
   * Derive observed attributes based on properties defined in "reactiveAttributes".
   * Since this is called by the platform once per class, before construction,
   * we also use this as an opportunity to set up all property getter/setters.
   * @returns { Array<string> }
   */
  static get observedAttributes() {
    if (this._observedAttributes === undefined) {
      this._reactiveAttributes = this.reactiveAttributes;
      this._reactiveProperties = this.reactiveProperties;
      this._observedAttributes = [];

      // Force include "hidden" to allow hooking into update lifecycle
      if (!('hidden' in this._reactiveAttributes)) {
        this._reactiveAttributes.hidden = Boolean;
      }
      // Force include "isConnected" to allow hooking into update lifecycle
      if (!('isConnected' in this._reactiveProperties)) {
        this._reactiveProperties.isConnected = Boolean;
      }

      for (const propName in this._reactiveAttributes) {
        const attrName = propName.toLowerCase();

        if (GLOBAL_ATTRIBUTES.includes(attrName)) {
          throw Error(`"${attrName}" is a reserved global attribute name and cannot be made reactive`);
        }

        const type = this._reactiveAttributes[propName];

        if (type === ReactiveState) {
          throw Error(`ReactiveState instances cannot be set as attributes`);
        }

        this._attributeToPropertyMap.set(attrName, propName);
        this._observedAttributes.push(attrName);
        Object.defineProperty(this.prototype, propName, this._getReactivePropertyDescription(propName, type));
      }

      for (const propName in this._reactiveProperties) {
        Object.defineProperty(
          this.prototype,
          propName,
          this._getReactivePropertyDescription(propName, this._reactiveProperties[propName]),
        );
      }

      // Wait task tick otherwise this call will error if it triggers additional component registrations
      Promise.resolve().then(() => this.whenDefinedCallback());
    }

    return this._observedAttributes;
  }

  /**
   * Called after this component class has been defined
   */
  static whenDefinedCallback() {}

  /**
   * Setup reactive property getter/setter
   * @param { string } propName
   * @param { ReactiveType } type
   */
  static _getReactivePropertyDescription(propName, type) {
    return {
      /**
       * @this { ReactiveElement }
       * @returns { unknown }
       */
      get() {
        // @ts-ignore
        return this[`__${propName}`];
      },
      /**
       * @this { ReactiveElement }
       * @param { unknown } value
       */
      set(value) {
        this.propertyChangedCallback(propName, value);
      },
      configurable: true,
      enumerable: true,
    };
  }

  constructor() {
    super();
    /** Flag indicating that the element has been connected and instantiated for the first time */
    this.isBootstrapped = false;
    /** Flag indicating that the element is parsed and all reactive properties have been initialised */
    this.isReady = false;
    /** Flag indicating whether an update has been requested */
    this.isUpdatePending = false;
    /** @type { ChangedProperties | undefined } */
    this.lastChangedProperties;

    /** @type { ChangedProperties } */
    this._changedProperties = new Map();
    /** @type { Set<string> } */
    this._executingHooks = new Set();
    /** @type { Set<ReactiveController> } */
    this._reactiveControllers = new Set();
    /** @type { Map<string, () => void> } } */
    this._reactiveStateSubscriptions = new Map();
    this._reflecting = false;
    /** @type { Set<string> } */
    this._reflectingProperties = new Set();

    // Due to bugs in Chrome(?) where some events aren't run through customElement.handleEvent,
    // force bind to instance
    if (this.handleEvent !== undefined) {
      // @ts-ignore
      this.handleEvent = this.handleEvent.bind(this);
    }

    const staticClass = /** @type { typeof ReactiveElement } */ (this.constructor);
    const reactiveAttributesAndProperties = { ...staticClass._reactiveAttributes, ...staticClass._reactiveProperties };
    /** @type { Record<string, unknown> } */
    const propertiesToUpgrade = {};

    for (const propName in reactiveAttributesAndProperties) {
      // @ts-ignore
      // Define backing property
      this[`__${propName}`] = undefined;
      // Attributes/properties set before class definition will need to be "upgraded" after definition
      if (this.hasOwnProperty(propName)) {
        // @ts-ignore
        propertiesToUpgrade[propName] = this[propName];
      }
    }

    // Make sure initial update Promise is "pending" until bootstrapped
    /** @type { (value?: any) => void } */
    this._updatePromiseResolve;
    /** @type { Promise<boolean> } */
    this._updatePromise = new Promise((resolve) => (this._updatePromiseResolve = resolve));

    // Wait until parsed to ensure that all constructors have run and we are "ready"
    whenElementParsed(this).then(() => {
      this.isReady = true;

      for (const propName in reactiveAttributesAndProperties) {
        if (this.hasOwnProperty(propName)) {
          // Property definition found, so handle same as upgrade
          // This includes native class fields using define semantics
          if (!(propName in propertiesToUpgrade)) {
            const key = `__${propName}`;
            // If reactive property value already set via attribute,
            // storing the default value will overwrite, so copy from `key` instead
            propertiesToUpgrade[propName] =
              // @ts-ignore
              this.hasAttribute(propName.toLowerCase()) && this[key] != null ? this[key] : this[propName];
          }
        } else {
          this._validateReactiveProperty(propName);
        }

        if (propName in propertiesToUpgrade) {
          // Property definitions cannot be cleared, only overwritten,
          // so overwrite with same definition as super
          Object.defineProperty(
            this,
            propName,
            staticClass._getReactivePropertyDescription(propName, reactiveAttributesAndProperties[propName]),
          );

          // @ts-ignore
          // Apply initial values from upgraded properties
          this[propName] = propertiesToUpgrade[propName];
        }
      }

      this.readyCallback();

      // If `connectedCallback` was called before ready, bootstrap now
      if (this.isConnected) {
        this.bootstrap();
      }
    });
  }

  /**
   * Called when the element is parsed and all reactive properties have been initialised.
   */
  readyCallback() {
    // Abstract
  }

  connectedCallback() {
    this.isConnected = true;

    for (const controller of this._reactiveControllers) {
      controller.hostConnected?.();
    }

    this.bootstrap();
  }

  /**
   * Trigger first update lifecycle after ready and connected.
   */
  bootstrap() {
    if (!this.isReady || !this.isConnected || this.isBootstrapped) {
      return;
    }

    // Initial updates have been suppressed until ready
    // to avoid triggering side-effects before subclass constructors are run,
    // or attributes and child elements have been parsed
    for (const [propName, oldValue] of this._changedProperties) {
      // @ts-ignore
      const value = this[propName];

      // Handle initial nested ReactiveState updates
      if (value instanceof ReactiveState) {
        const stateChangedProperties = /** @type { ChangedProperties } */ (oldValue);

        for (const [statePropName, stateOldValue] of stateChangedProperties) {
          this.updating(`${propName}.${statePropName}`, stateOldValue);
        }
      }
      this.updating(propName, oldValue);
    }

    // Resolve pending initial `updateComplete`
    this._updatePromiseResolve();

    this._invalidate();
  }

  /**
   * Handle attribute change.
   * Triggered by platform.
   * @param { string } attrName
   * @param { unknown } oldValue
   * @param { unknown } newValue
   */
  attributeChangedCallback(attrName, oldValue, newValue) {
    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    const propName = constructor._attributeToPropertyMap.get(attrName);

    if (!this._reflecting && propName !== undefined && oldValue !== newValue) {
      if (!this.isBootstrapped) {
        this._validateReactiveProperty(propName);
      }

      const type = constructor._reactiveAttributes[propName];
      const value = attributeValueToProperty(newValue, type);

      // Distinguish between calls to setReactiveAttribute() => attributeChangedCallback()
      // from inside or outside the hook function
      if (!this._executingHooks.has(attrName) && !this._executeHook(attrName, value, type)) {
        this.setReactiveProperty(propName, value);
      }
    }
  }

  /**
   * Wrap default to validate attribute name for correct casing.
   * @param { string } attrName
   * @param { string } value
   */
  setAttribute(attrName, value) {
    this._validateAttributeName(attrName);
    super.setAttribute(attrName, value);
  }

  /**
   * Store "value" for reactive attribute named "attrName".
   * Should be called from a reactive property hook function to apply attribute changes.
   * @param { string } attrName
   * @param { unknown } value
   */
  setReactiveAttribute(attrName, value) {
    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    const attributes = constructor._reactiveAttributes;
    const propName = this._validateAttributeName(attrName);

    if (!propName) {
      throw Error(`"${attrName}" is not a registered reactive attribute`);
    }

    if (value !== undefined) {
      const attrValue = propertyValueToAttribute(value, attributes[propName]);

      if (attrValue !== undefined && attrName !== undefined) {
        if (attrValue === null) {
          super.removeAttribute(attrName);
        } else {
          super.setAttribute(attrName, attrValue);
        }
      }
    }
  }

  /**
   * Handle property change.
   * Triggered by property getter.
   * @param { string } propName
   * @param { unknown } value
   */
  propertyChangedCallback(propName, value) {
    // Skip running update lifecycle for initial "undefined"
    if (!this.isBootstrapped && value === undefined) {
      return;
    }

    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    // Support running hooks in "reactiveAttributes" triggered by property change by retrieving from both
    const type = constructor._reactiveProperties[propName] ?? constructor._reactiveAttributes[propName];

    if (!this._executeHook(propName, value, type)) {
      const changed = this.setReactiveProperty(propName, value);

      if (changed && this.isReactiveAttribute(propName)) {
        this._reflectingProperties.add(propName.toLowerCase());
      }
    }
  }

  /**
   * Store "value" for reactive property named "propName".
   * Should be called from a reactive property hook function to apply property changes.
   * Returns "true" if changed.
   * @param { string } propName
   * @param { unknown } value
   * @returns { boolean }
   */
  setReactiveProperty(propName, value) {
    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    const attributes = /** @type { ReactiveAttributes } */ (constructor._reactiveAttributes);
    const properties = /** @type { ReactiveProperties } */ (constructor._reactiveProperties);

    if (propName in properties || propName in attributes) {
      const propType = properties[propName];
      const key = `__${propName}`;
      // @ts-ignore
      let oldValue = this[key];
      const changed = !isEquivalent(oldValue, value);

      if (changed) {
        // @ts-ignore
        this[key] = value;

        if (propType === ReactiveState) {
          if (oldValue) {
            const unsubscribe = this._reactiveStateSubscriptions.get(propName);

            if (unsubscribe) {
              unsubscribe();
              this._reactiveStateSubscriptions.delete(propName);
            }
          }
          if (value instanceof ReactiveState) {
            this._reactiveStateSubscriptions.set(
              propName,
              // Callback will be triggered immediately with any properties that have defined initial values
              value.subscribe((statePropName, stateOldValue) => {
                if (!this._changedProperties.has(propName)) {
                  this._changedProperties.set(propName, new Map());
                }

                const stateChangedProperties = /** @type { ChangedProperties } */ (
                  this._changedProperties.get(propName)
                );

                stateChangedProperties.set(statePropName, stateOldValue);

                // Supress calls to "updating()" until ready to avoid side-effects during bootstrap processing.
                // If the element is cached after disconnect and then reconnected,
                // the constructor won't be run, so define readiness to include bootstrap state
                // (which won't change due to disconnect)
                if (this.isBootstrapped) {
                  this.updating(`${propName}.${statePropName}`, stateOldValue);
                  this._invalidate();
                }
              }),
            );
          }
        }

        // Store original oldValue in cases where called multiple times in same update cycle
        if (!this._changedProperties.has(propName)) {
          this._changedProperties.set(propName, oldValue);
        }
        // Supress calls to "updating()" until ready to avoid side-effects during bootstrap processing.
        // If the element is cached after disconnect and then reconnected,
        // the constructor won't be run, so define readiness to include bootstrap state
        // (which won't change due to disconnect)
        if (this.isBootstrapped) {
          this.updating(propName, oldValue);
          this._invalidate();
        }
      }

      return changed;
    } else {
      throw Error(`"${propName}" is not a registered reactive property`);
    }
  }

  /**
   * Determine if "propName" is registered as a reactive attribute
   * @param { string | undefined } propName
   */
  isReactiveAttribute(propName) {
    if (propName === undefined) {
      return false;
    }
    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    return propName in constructor._reactiveAttributes;
  }

  /**
   * Determine if "propName" is registered as a reactive property
   * @param { string | undefined } propName
   */
  isReactiveProperty(propName) {
    if (propName === undefined) {
      return false;
    }
    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    return propName in constructor._reactiveProperties;
  }

  /**
   * Called during update lifecycle immediately after attribute or property change.
   * Setting properties inside this method will *not* trigger a new update.
   * Suitable for deriving state values or side-effects.
   * @param { string } propName
   * @param { unknown } oldValue
   */
  updating(propName, oldValue) {}

  /**
   * Called during update lifecycle after all attribute or property changes.
   * Setting reactive properties inside this method will *not* trigger a new update cycle.
   * Suitable for deriving state values or side-effects.
   * @param { ChangedProperties } changedProperties
   */
  update(changedProperties) {}

  /**
   * Determine if "propName" reactive property has changed since last update cycle
   * @param { string } propName
   * @returns { boolean }
   */
  hasUpdated(propName) {
    return this.lastChangedProperties?.has(propName) ?? false;
  }

  /**
   * Called after all updates triggered by attribute or property changes.
   * Updates are batched in the same micro-task.
   * Setting reactive properties inside this method *will* trigger a new update cycle.
   * @param { ChangedProperties } changedProperties
   */
  updated(changedProperties) {}

  /**
   * Called after first call to updated() during first bootstrap update cycle.
   * @param { ChangedProperties } changedProperties
   */
  firstUpdated(changedProperties) {}

  /**
   * Force manual update without corresponding property change
   */
  forceUpdate() {
    this._invalidate();
  }

  /**
   * Satisfy "ReactiveControllerHost" API
   */
  requestUpdate() {
    this.forceUpdate();
  }

  /**
   * Retrieve Promise that resolves when element has completed updating
   * @returns { Promise<boolean> }
   */
  get updateComplete() {
    return this._updatePromise;
  }

  /**
   * Pretty print "changedProperties" to console
   * @param { ChangedProperties } changedProperties
   */
  logChangedProperties(changedProperties) {
    console.log(
      `${this.nodeName}\n` +
        Array.from(changedProperties.keys())
          .map((key) => {
            /** @type { { [key: string]: any } } */
            const values = {
              from: changedProperties.get(key),
              // @ts-ignore
              to: this[key],
            };
            for (const prop in values) {
              if (typeof values[prop] === 'object') {
                values[prop] = Array.isArray(values[prop]) ? `[Array(${values[prop].length})]` : '{Object}';
              }
            }
            return `${key}: ${values.from} => ${values.to}`;
          })
          .join('\n'),
    );
  }

  /**
   * Register a "ReactiveController" to participate in element's reactive update cycle
   * @param { ReactiveController } controller
   */
  addController(controller) {
    this._reactiveControllers.add(controller);
    if (this.isConnected && this.isBootstrapped) {
      controller.hostConnected?.();
    }
  }

  /**
   * Unregister a "ReactiveController"
   * @param { ReactiveController } controller
   */
  removeController(controller) {
    this._reactiveControllers.delete(controller);
  }

  disconnectedCallback() {
    this.isConnected = false;
    for (const controller of this._reactiveControllers) {
      controller.hostDisconnected?.();
    }
  }

  /**
   * Validate attribute name casing
   * @param { string } attrName
   * @private
   */
  _validateAttributeName(attrName) {
    if (isMinified) {
      return attrName;
    }

    const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);
    const attributes = constructor._reactiveAttributes;
    const propName = constructor._attributeToPropertyMap.get(attrName);

    if (propName && propName in attributes) {
      return propName;
    }

    const lcAttrName = attrName.toLowerCase();

    if (attrName !== lcAttrName) {
      const propName = constructor._attributeToPropertyMap.get(lcAttrName);

      if (propName) {
        throw Error(`ReactiveElement attribute names must be lowercase: ${attrName}`);
      }
    }
  }

  /**
   * Validate that reactive property has been defined on the instance
   * @param { string } propName
   * @private
   */
  _validateReactiveProperty(propName) {
    if (isMinified) {
      return;
    }

    const protoHasOwnPropDef = Object.getPrototypeOf(this).hasOwnProperty(propName);
    const hasPropKey = `__${propName}` in this;
    const hasOwnPropDef = this.hasOwnProperty(propName);

    // If reactive property has been defined, but there is no property key
    // or no property definition (Object.defineProperty) because initial value has not been set,
    // we are missing an instance property intialisation
    if (protoHasOwnPropDef && !hasPropKey && !hasOwnPropDef && propName !== 'hidden' && propName !== 'isConnected') {
      console.warn(`missing ReactiveElement (${this.nodeName}) instance property definition for: ${propName}`);
    }
  }

  /**
   * Execute reactive property hook
   * @param { string } attrPropName
   * @param { unknown } value
   * @param { ReactiveType } hook
   * @returns { boolean }
   * @private
   */
  _executeHook(attrPropName, value, hook) {
    if (isHookPropertyType(hook)) {
      this._executingHooks.add(attrPropName);

      if (typeof hook === 'string') {
        // @ts-ignore
        if (hook in this && typeof this[hook] === 'function') {
          // @ts-ignore
          this[hook].call(this, value);
        }
      } else if (typeof hook === 'function') {
        // @ts-ignore
        hook.call(this, this, value);
      }

      this._executingHooks.delete(attrPropName);

      return true;
    }

    return false;
  }

  /**
   * Invalidate change state and delay validation until
   * the current "_updatePromise" has resolved.
   * @private
   */
  async _invalidate() {
    if (!this.isUpdatePending) {
      this.isUpdatePending = true;

      const previousUpdatePromise = this._updatePromise;
      let resolver;

      this._updatePromise = new Promise((resolve) => (resolver = resolve));
      await previousUpdatePromise;

      const changedProperties = (this.lastChangedProperties = this._changedProperties);
      const constructor = /** @type { typeof ReactiveElement } */ (this.constructor);

      this._reflecting = true;
      for (const attrName of this._reflectingProperties) {
        // @ts-ignore
        this.setReactiveAttribute(attrName, this[constructor._attributeToPropertyMap.get(attrName)]);
      }
      this._reflecting = false;
      this._reflectingProperties.clear();

      // Allow any reactive property changes triggered here to be applied in this same update cycle
      for (const controller of this._reactiveControllers) {
        controller.hostUpdate?.();
      }
      this.update(changedProperties);

      const isFirstUpdate = !this.isBootstrapped;
      this._changedProperties = new Map();
      this.isUpdatePending = false;
      // Set `isBootstrapped` before calling `updated` to ensure that
      // any additional changes will trigger new update cycle
      this.isBootstrapped = true;

      // Reactive property changes triggered here will start new update cycle
      for (const controller of this._reactiveControllers) {
        controller.hostUpdated?.();
      }

      this.updated(changedProperties);

      // @ts-ignore
      resolver(this.isUpdatePending);

      if (isFirstUpdate) {
        this.firstUpdated(changedProperties);
      }
    }
  }
}

export { ReactiveElement, ReactiveState, isEquivalent };
